import Config from '@config';
import { useSelector } from 'react-redux';

import { injectSupplySlice } from '@/app/supplyApi';
import { FiltersGrid, Pagination } from '@/features/types';
import {
  cachedItemByIdSelector,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@/features/utils';

import {
  CreateOrganizationRequest,
  OrganizationListItemReadModel,
  OrganizationReadModel,
  UpdateOrganizationRequest,
} from './types';

export interface CreateOrganizationArgs {
  data: CreateOrganizationRequest;
  logo?: File | null;
}

export interface UpdateOrganizationArgs {
  data: UpdateOrganizationRequest;
  logo?: File | null;
}

export const organizationApi = injectSupplySlice({
  baseUrl: Config.VITE_API_URL,
  tagTypes: ['Organization'],
  endpoints: builder => ({
    getOrganizations: builder.infiniteQuery<
      OrganizationListItemReadModel[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/organizations/query',
        method: 'GET',
        params: { ...pageParam, ...queryArg },
        headers: { organizationId: '' },
      }),
      providesTags: () => ['Organization'],
    }),
    createOrganization: builder.mutation<string, CreateOrganizationArgs>({
      query: ({ data, logo }) => {
        const formData = new FormData();
        formData.append('json', JSON.stringify(data));
        if (logo) {
          formData.append('logo', logo, logo.name);
        }

        return {
          url: '/organizations',
          method: 'POST',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        };
      },
      invalidatesTags: ['Organization'],
    }),
    getOrganizationById: builder.query<OrganizationReadModel, string>({
      query: id => ({ url: `/organizations/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Organization', id }],
    }),
    getOrganizationLogo: builder.query<Blob, string>({
      query: id => ({
        url: `/organizations/${id}/logo`,
        method: 'GET',
        responseType: 'blob',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Organization', id }],
    }),
    updateOrganization: builder.mutation<void, UpdateOrganizationArgs>({
      query: ({ data, logo }) => {
        const formData = new FormData();
        formData.append('json', JSON.stringify(data));
        if (logo) {
          formData.append('logo', logo, logo.name);
        }

        return {
          url: `/organizations/${data.id}`,
          method: 'PUT',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        };
      },
      invalidatesTags: (_result, _error, { data }) => [
        { type: 'Organization', id: data.id },
        'Organization',
      ],
    }),
  }),
});

export const useGetOrganizationsInfiniteQuery = normalizeInfiniteQueryData(
  organizationApi.useGetOrganizationsInfiniteQuery,
);

const getCachedOrganizationByIdSelector = cachedItemByIdSelector(
  organizationApi.reducerPath,
  'getOrganizations',
);

export const useGetCachedOrganizationByIdSelector = (id: string) =>
  useSelector(getCachedOrganizationByIdSelector(id));

export const {
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetOrganizationByIdQuery,
  useGetOrganizationLogoQuery,
} = organizationApi;
